import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Brain, Mail, Lock, Eye, EyeOff, User, Phone, ArrowRight, Check, Sparkles } from 'lucide-react';
import { useToast } from '../../components/ui/toast';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    accept_terms: false,
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      // Store in localStorage for later use
      localStorage.setItem('referral_code', refCode);
    } else {
      // Check localStorage
      const storedRef = localStorage.getItem('referral_code');
      if (storedRef) setReferralCode(storedRef);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      addToast('Şifreler eşleşmiyor', 'error');
      return;
    }

    if (formData.password.length < 6) {
      addToast('Şifre en az 6 karakter olmalı', 'error');
      return;
    }

    if (!formData.accept_terms) {
      addToast('Kullanım koşullarını kabul etmelisiniz', 'error');
      return;
    }

    setLoading(true);

    try {
      // Track referral if exists
      if (referralCode) {
        try {
          await fetch(`${API_URL}/api/referral/track?referrer_code=${referralCode}&referred_email=${encodeURIComponent(formData.email)}`, {
            method: 'POST'
          });
        } catch (e) {
          console.log('Referral tracking failed:', e);
        }
      }

      await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
      });
      
      // Clear referral code from localStorage after successful registration
      localStorage.removeItem('referral_code');
      
      addToast('Kayıt başarılı! Hoş geldiniz.', 'success');
      navigate('/panel-erisim');
    } catch (error) {
      addToast(error.message || 'Kayıt başarısız', 'error');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Masterclass ile yolculuğunuza başlayın',
    'Eğitim satın alarak panele tam erişim kazanın',
    'Zihin Puanı ve seviye sistemi',
    'Özel topluluk ve network erişimi',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-purple-950/30 to-gray-950 flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg relative"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <span className="text-xl font-bold text-white">Bilinçaltı Dönüşüm</span>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white">Hesap Oluşturun</h1>
            <p className="mt-2 text-white/50">Dönüşüm yolculuğunuza başlayın</p>
          </div>

          {/* Referral Banner */}
          {referralCode && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="text-amber-400 font-medium">Davet Koduyla Geldiniz!</p>
                  <p className="text-white/60 text-sm">Kayıt olduğunuzda sizi davet eden kişi Zihin Puanı kazanacak.</p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Benefits */}
          <div className="mb-8 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
            <p className="text-purple-400 font-medium mb-3">Üyelik Avantajları:</p>
            <div className="grid grid-cols-2 gap-2">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                  <Check className="w-4 h-4 text-purple-400" />
                  {benefit}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white/60 text-sm mb-2">Ad</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Adınız"
                    required
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
                  />
                </div>
              </div>
              <div>
                <label className="block text-white/60 text-sm mb-2">Soyad</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Soyadınız"
                  required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">E-posta</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ornek@email.com"
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Telefon</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="05XX XXX XX XX"
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="En az 6 karakter"
                  required
                  className="w-full pl-12 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-white/60 text-sm mb-2">Şifre Tekrar</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <input
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  placeholder="Şifrenizi tekrar girin"
                  required
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all"
                />
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.accept_terms}
                onChange={(e) => setFormData({ ...formData, accept_terms: e.target.checked })}
                className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-purple-500 focus:ring-purple-500"
              />
              <span className="text-white/50 text-sm">
                <Link to="/kullanim-kosullari" className="text-purple-400 hover:text-purple-300">Kullanım Koşulları</Link>
                {' '}ve{' '}
                <Link to="/gizlilik" className="text-purple-400 hover:text-purple-300">Gizlilik Politikası</Link>
                'nı okudum ve kabul ediyorum.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white font-semibold hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Hesap Oluştur
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/50">
              Zaten hesabınız var mı?{' '}
              <Link to="/giris" className="text-purple-400 hover:text-purple-300 font-medium">
                Giriş Yapın
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
